import { useEffect, useState } from 'react'
import { onboardingApi, type OnboardingSurvey } from '@/api/auth'
import { Stepper } from '@/components/onboarding/Stepper'

/**
 * US-E2-002 — Onboarding survey (диалог-анкета).
 * AC #1: save-on-step (PATCH per step) + валидация.
 * AC #2: цель на 24ч — текстовое поле, акционер сам формулирует.
 * AC #3: артефакт от CEO — отложен в DT (требует Anthropic + Temporal).
 *
 * Вопросы 1-8 — placeholder, акционер поменяет позже.
 */

interface Step {
  id: string
  question: string
  hint?: string
  // Какое поле survey обновлять. answers.<key> или прямое колонка goal_*.
  field: { kind: 'answer'; key: string } | { kind: 'goal_24h' } | { kind: 'goal_30d' }
  multiline?: boolean
}

const STEPS: Step[] = [
  {
    id: 'stage',
    question: 'Что у тебя сейчас?',
    hint: 'Идея, есть бизнес, или стартап в работе.',
    field: { kind: 'answer', key: 'stage' },
  },
  {
    id: 'niche',
    question: 'Ниша или отрасль',
    hint: 'Кратко: e-commerce, SaaS, агентство, услуги.',
    field: { kind: 'answer', key: 'niche' },
  },
  {
    id: 'company_size',
    question: 'Размер компании, если уже есть',
    hint: 'Обороты, людей в команде. Можно «нет, идея».',
    field: { kind: 'answer', key: 'company_size' },
    multiline: true,
  },
  {
    id: 'pain',
    question: 'Главная боль или задача',
    hint: 'Что больше всего мешает прямо сейчас?',
    field: { kind: 'answer', key: 'pain' },
    multiline: true,
  },
  {
    id: 'goal_24h',
    question: 'Цель на ближайшие 24 часа',
    hint: 'Что хочешь получить от CEO к концу суток?',
    field: { kind: 'goal_24h' },
    multiline: true,
  },
  {
    id: 'goal_30d',
    question: 'Цель на 30 дней',
    hint: 'Куда хочешь прийти за месяц.',
    field: { kind: 'goal_30d' },
    multiline: true,
  },
  {
    id: 'context',
    question: 'Контекст для CEO',
    hint: 'Свободный текст: миссия, ценности, табу, что важно знать.',
    field: { kind: 'answer', key: 'context' },
    multiline: true,
  },
  {
    id: 'confirm',
    question: 'Готов начать?',
    hint: 'CEO получит твои ответы и приступит к работе.',
    field: { kind: 'answer', key: 'confirm' },
  },
]

export function Survey() {
  const [stepIdx, setStepIdx] = useState(0)
  const [survey, setSurvey] = useState<OnboardingSurvey | null>(null)
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initial load
  useEffect(() => {
    onboardingApi.getSurvey().then((s) => {
      setSurvey(s)
      hydrateStep(0, s)
    }).catch(() => {
      setSurvey({ answers: {} })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function hydrateStep(idx: number, s: OnboardingSurvey | null) {
    if (!s) return setValue('')
    const stp = STEPS[idx]!
    if (stp.field.kind === 'answer') {
      const v = s.answers[stp.field.key]
      setValue(typeof v === 'string' ? v : '')
    } else if (stp.field.kind === 'goal_24h') {
      setValue(s.goal_24h ?? '')
    } else {
      setValue(s.goal_30d ?? '')
    }
  }

  const step = STEPS[stepIdx]!
  const isLast = stepIdx === STEPS.length - 1
  const required = step.field.kind === 'goal_24h' || step.id === 'confirm'

  async function saveCurrent(): Promise<OnboardingSurvey | null> {
    setError(null)
    if (required && !value.trim()) {
      setError('Это поле обязательно для следующего шага.')
      return null
    }
    setSubmitting(true)
    try {
      const patch: Partial<OnboardingSurvey> = {}
      if (step.field.kind === 'answer') {
        patch.answers = { [step.field.key]: value }
      } else if (step.field.kind === 'goal_24h') {
        patch.goal_24h = value
      } else if (step.field.kind === 'goal_30d') {
        patch.goal_30d = value
      }
      const updated = await onboardingApi.patchSurvey(patch)
      setSurvey(updated)
      return updated
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить.')
      return null
    } finally {
      setSubmitting(false)
    }
  }

  async function onNext() {
    const updated = await saveCurrent()
    if (!updated) return
    if (isLast) {
      // Финал — POST /complete и redirect.
      try {
        setSubmitting(true)
        await onboardingApi.completeSurvey()
        window.location.assign('/')
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось завершить.')
        setSubmitting(false)
      }
      return
    }
    const nextIdx = stepIdx + 1
    setStepIdx(nextIdx)
    hydrateStep(nextIdx, updated)
  }

  function onBack() {
    if (stepIdx === 0) return
    const prevIdx = stepIdx - 1
    setStepIdx(prevIdx)
    hydrateStep(prevIdx, survey)
  }

  return (
    <main className="min-h-screen flex items-start justify-center bg-surface-canvas px-4 py-12">
      <div className="w-full max-w-2xl space-y-8">
        <Stepper current={stepIdx + 1} total={STEPS.length} />

        <div className="space-y-3">
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-text-primary tracking-[-0.02em]">
            {step.question}
          </h1>
          {step.hint && (
            <p className="text-sm text-text-secondary">{step.hint}</p>
          )}
        </div>

        {step.multiline ? (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={5}
            placeholder="Ответ"
            className="w-full px-4 py-3 rounded-lg border border-border bg-surface-subtle text-text-primary placeholder:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 transition-colors"
          />
        ) : (
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Ответ"
            className="w-full px-4 py-3 rounded-lg border border-border bg-surface-subtle text-text-primary placeholder:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 transition-colors"
          />
        )}

        {error && (
          <p role="alert" className="text-sm text-status-danger-text">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={stepIdx === 0 || submitting}
            className="px-5 py-2.5 rounded-lg border border-border bg-surface-canvas text-text-primary font-medium hover:bg-surface-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Назад
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={submitting}
            className="px-6 py-2.5 rounded-lg bg-action-primary-bg text-text-on-brand font-semibold hover:bg-action-primary-bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Сохраняем…' : isLast ? 'Завершить' : 'Дальше'}
          </button>
        </div>
      </div>
    </main>
  )
}
